import { useMutation, useQuery, useQueryClient } from "react-query";
import { authHeaders } from "../authentication/auth-header";
import { getApiErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { AxiosError } from "axios";
import { useSessionContext } from "../authentication/session.context";
import {
  CreateTransactionModel,
  DeleteTransactionModel,
  GetResponseTransactionModel,
  MutateResponseTransactionModel,
  QueryKeyTransaction,
  UpdateTransactionModel,
  deleteTransactionController,
  createTransactionController,
  getTransactionController,
  updateTransactionController,
  completeTransactionController,
  cancelTransactionController,
  dashboardTransactionController,
  GetDashboardResponseTransactionModel,
  payTransactionController,
} from "@factory/model/query-model-transaction";
import { DashboardTransactionData } from "@factory/model/query-model-transaction";

export function useQyGetTransaction(
  search: string,
  limit = 99,
  offset = 0,
  order?: object,
  filter?: Record<string, string>,
  dateFilter?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseTransactionModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    search: string | undefined = undefined,
    limit: number | undefined = undefined,
    offset: number | undefined = undefined,
    order: object | undefined = undefined,
    filter: Record<string, string> | undefined = undefined,
    dateFilter: string | undefined = undefined,
    startDate: string | undefined = undefined,
    endDate: string | undefined = undefined,
  ) => {
    await checkSession();

    showProgress();
    const operation = await getTransactionController(
      search,
      limit,
      offset,
      order,
      { filter: JSON.stringify(filter) },
      dateFilter,
      startDate,
      endDate,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseTransactionModel;
  };

  return useQuery({
    enabled,
    queryKey: [
      QueryKeyTransaction,
      search,
      limit,
      offset,
      order,
      filter,
      dateFilter,
    ],
    queryFn: () => apiFn(search, limit, offset, order, filter, dateFilter),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetTransactionById(
  id: string,
  onSuccess?:
    | ((data: GetResponseTransactionModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (id: string, search = "", limit = 1, offset = 0) => {
    await checkSession();

    showProgress();
    const operation = await getTransactionController(
      search,
      limit,
      offset,
      undefined,
      { filter: JSON.stringify({ code: id }) },
      undefined,
      undefined,
      undefined,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response["data"] as GetResponseTransactionModel;
  };

  return useQuery({
    queryKey: [QueryKeyTransaction, id],
    queryFn: () => apiFn(id),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyCreateTransaction(
  onSuccess?:
    | ((data: MutateResponseTransactionModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: CreateTransactionModel) => {
    await checkSession();

    showProgress();
    const operation = await createTransactionController(payload, authHeaders());
    const response = (await operation()).data;
    return response as MutateResponseTransactionModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyTransaction);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyUpdateTransaction(
  onSuccess?:
    | ((data: MutateResponseTransactionModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: UpdateTransactionModel) => {
    await checkSession();

    showProgress();
    const operation = await updateTransactionController(payload, authHeaders());
    const response = (await operation()).data;
    return response["message"] as MutateResponseTransactionModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyTransaction);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyDeleteTransaction(
  onSuccess?:
    | ((data: MutateResponseTransactionModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: DeleteTransactionModel) => {
    await checkSession();

    showProgress();
    const operation = await deleteTransactionController(payload, authHeaders());
    const response = (await operation()).data;
    return response["message"] as MutateResponseTransactionModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyTransaction);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyCompleteTransaction(
  onSuccess?:
    | ((data: MutateResponseTransactionModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: DeleteTransactionModel) => {
    await checkSession();

    showProgress();
    const operation = await completeTransactionController(
      payload,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response["message"] as MutateResponseTransactionModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyTransaction);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyCancelTransaction(
  onSuccess?:
    | ((data: MutateResponseTransactionModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: DeleteTransactionModel) => {
    await checkSession();

    showProgress();
    const operation = await cancelTransactionController(payload, authHeaders());
    const response = (await operation()).data;
    return response["message"] as MutateResponseTransactionModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyTransaction);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetDashboardTransaction(
  search: string,
  filter?: Record<string, string>,
  dateFilter?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: DashboardTransactionData) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    search: string | undefined = undefined,
    filter: Record<string, string> | undefined = undefined,
    dateFilter: string | undefined = undefined,
    startDate: string | undefined = undefined,
    endDate: string | undefined = undefined,
  ) => {
    await checkSession();

    showProgress();
    const operation = await dashboardTransactionController(
      search,
      filter,
      dateFilter,
      startDate,
      endDate,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response["data"] as unknown as DashboardTransactionData;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyTransaction, search, filter, dateFilter],
    queryFn: () => apiFn(search, filter, dateFilter),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyPayTransaction(
  onSuccess?:
    | ((data: MutateResponseTransactionModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: DeleteTransactionModel) => {
    await checkSession();

    showProgress();
    const operation = await payTransactionController(payload, authHeaders());
    const response = (await operation()).data;
    return response["message"] as MutateResponseTransactionModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyTransaction);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}
